import { functions, httpsCallable } from "../firebase";
import { isGoogleSlidesPresentationUrl } from "./googleSlidesLinks";
import { getGoogleDriveOAuthRedirectUri } from "./oauthRedirectUri";

export function getGoogleDriveRedirectUri() {
  return getGoogleDriveOAuthRedirectUri();
}

/** Prefer getGoogleDriveRedirectUri() at call time so host wins over a bad baked env. */
export const GOOGLE_DRIVE_DEFAULT_REDIRECT_URI = "https://lessonlaunchpad.com/google-drive-callback";

export const GOOGLE_MIME_TYPES = {
  DOCUMENT: "application/vnd.google-apps.document",
  PRESENTATION: "application/vnd.google-apps.presentation",
  SPREADSHEET: "application/vnd.google-apps.spreadsheet",
  FOLDER: "application/vnd.google-apps.folder",
};

export const GOOGLE_PICKER_MODES = {
  PRESENTATION: "presentation",
  DOCUMENT_OR_SPREADSHEET: "documentOrSpreadsheet",
  FOLDER: "folder",
};

const GOOGLE_PICKER_SCRIPT_ID = "google-picker-api-script";
export const GOOGLE_DRIVE_AUTH_MESSAGE_TYPE = "lesson-launchpad:google-drive-auth";

export function hasGoogleDriveConnection(user) {
  return Boolean(
    user?.googleDriveAuth?.accessToken ||
    user?.googleDriveAuth?.refreshToken ||
    user?.googleDriveAuth?.connectedAt
  );
}

export function isGoogleDriveReconnectRequiredError(error) {
  const code = String(error?.code || "");
  const message = String(error?.message || "").toLowerCase();
  return (
    code === "functions/failed-precondition" ||
    code === "functions/not-found" ||
    code === "functions/permission-denied" ||
    message.includes("reconnect google drive") ||
    message.includes("google drive connection expired") ||
    message.includes("no google drive refresh token") ||
    message.includes("no google drive account is connected") ||
    message.includes("failed to refresh google drive access token")
  );
}

export function getGoogleMimeTypesForPickerMode(mode) {
  if (mode === GOOGLE_PICKER_MODES.PRESENTATION) {
    return [GOOGLE_MIME_TYPES.PRESENTATION];
  }

  if (mode === GOOGLE_PICKER_MODES.FOLDER) {
    return [GOOGLE_MIME_TYPES.FOLDER];
  }

  return [GOOGLE_MIME_TYPES.DOCUMENT, GOOGLE_MIME_TYPES.SPREADSHEET];
}

export function getGoogleFileKindFromMimeType(mimeType) {
  switch (mimeType) {
    case GOOGLE_MIME_TYPES.DOCUMENT:
      return "document";
    case GOOGLE_MIME_TYPES.PRESENTATION:
      return "presentation";
    case GOOGLE_MIME_TYPES.SPREADSHEET:
      return "spreadsheet";
    case GOOGLE_MIME_TYPES.FOLDER:
      return "folder";
    default:
      return "file";
  }
}

export function buildGoogleShareUrl(fileId, mimeType) {
  const trimmedFileId = String(fileId || "").trim();
  if (!trimmedFileId) return "";

  const encodedFileId = encodeURIComponent(trimmedFileId);
  switch (mimeType) {
    case GOOGLE_MIME_TYPES.DOCUMENT:
      return `https://docs.google.com/document/d/${encodedFileId}/edit?usp=sharing`;
    case GOOGLE_MIME_TYPES.PRESENTATION:
      return `https://docs.google.com/presentation/d/${encodedFileId}/edit?usp=sharing`;
    case GOOGLE_MIME_TYPES.SPREADSHEET:
      return `https://docs.google.com/spreadsheets/d/${encodedFileId}/edit?usp=sharing`;
    default:
      return `https://drive.google.com/file/d/${encodedFileId}/view?usp=sharing`;
  }
}

const GOOGLE_DOCS_HOST = "docs.google.com";
const GOOGLE_DOC_SHEET_PATH =
  /^\/(document|spreadsheets)\/(?:u\/\d+\/)?d\/(?!e(?:\/|$))[^/]+(?:\/|$)/i;

export function isValidGoogleSlidesShareUrl(value) {
  return isGoogleSlidesPresentationUrl(value);
}

export function isValidGoogleDocSheetShareUrl(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return false;

  try {
    const normalizedValue = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsedUrl = new URL(normalizedValue);
    const hostname = parsedUrl.hostname.replace(/^www\./i, "").toLowerCase();

    return hostname === GOOGLE_DOCS_HOST
      && GOOGLE_DOC_SHEET_PATH.test(parsedUrl.pathname);
  } catch (_) {
    return false;
  }
}

export function normalizeGoogleSlidesManualUrl(value) {
  const trimmed = String(value || "").trim();
  if (!isGoogleSlidesPresentationUrl(trimmed)) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function normalizeGoogleDocSheetManualUrl(value) {
  const trimmed = String(value || "").trim();
  if (!isValidGoogleDocSheetShareUrl(trimmed)) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function getGooglePickerSelection(fileMeta = {}) {
  if (!fileMeta?.fileId && !fileMeta?.id) return null;

  const fileId = fileMeta.fileId || fileMeta.id;
  const mimeType = fileMeta.mimeType || "";
  const shareUrl = fileMeta.shareUrl || buildGoogleShareUrl(fileId, mimeType);
  const slideCount = Number(fileMeta.slideCount);

  return {
    content: shareUrl,
    googleSource: "picker",
    googleFileId: fileId,
    googleTitle: fileMeta.name || fileMeta.title || "Untitled Google file",
    googleThumbnailUrl: fileMeta.thumbnailUrl || "",
    googleEditUrl: fileMeta.editUrl || fileMeta.webViewLink || shareUrl,
    googleMimeType: mimeType,
    googleFileKind: fileMeta.kind || getGoogleFileKindFromMimeType(mimeType),
    googleSharedWithAnyone: fileMeta.sharedWithAnyone === true,
    ...(Number.isFinite(slideCount) && slideCount > 0 ? { googleSlideCount: slideCount } : {}),
  };
}

export function getManualGoogleMetadataPatch(url) {
  return {
    content: url,
    googleSource: "manual",
    googleFileId: "",
    googleTitle: "",
    googleThumbnailUrl: "",
    googleEditUrl: "",
    googleMimeType: "",
    googleFileKind: "",
    googleSharedWithAnyone: false,
    googleSlideCount: null,
  };
}

export async function beginGoogleDriveAuth(redirectUri = getGoogleDriveRedirectUri()) {
  const beginFn = httpsCallable(functions, "googleDriveBeginAuth");
  const result = await beginFn({ redirectUri });
  return result.data;
}

/**
 * Authorize Drive without navigating the current Lesson Launchpad tab away.
 * The callback posts the result back to this window before closing itself.
 */
export async function connectGoogleDriveInPopup({ timeoutMs = 2 * 60 * 1000 } = {}) {
  if (typeof window === "undefined") {
    throw new Error("Google Drive authorization requires a browser window.");
  }

  const popup = window.open(
    "",
    "lesson-launchpad-google-drive-auth",
    "popup=yes,width=560,height=720,resizable=yes,scrollbars=yes"
  );
  if (!popup) {
    throw new Error("Could not open Google authorization. Please allow popups for Lesson Launchpad.");
  }

  try {
    popup.document.title = "Connecting Google Drive...";
    popup.document.body.innerHTML = `
      <div style="font-family:system-ui,-apple-system,sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;color:#004aad;font-weight:600;text-align:center;padding:24px;box-sizing:border-box;">
        Connecting Google Drive...
      </div>
    `;
  } catch (_) {
    // Navigation still works if a browser prevents writing to the placeholder.
  }

  let authorizationUrl;
  try {
    const result = await beginGoogleDriveAuth();
    authorizationUrl = result?.authorizationUrl;
    if (!authorizationUrl) {
      throw new Error("Google Drive authorization did not return a destination.");
    }
  } catch (error) {
    popup.close();
    throw error;
  }

  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      window.clearInterval(closedInterval);
      window.clearTimeout(timeout);
    };

    const finish = (callback) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };

    const handleMessage = (event) => {
      if (
        event.origin !== window.location.origin
        || event.source !== popup
        || event.data?.type !== GOOGLE_DRIVE_AUTH_MESSAGE_TYPE
      ) {
        return;
      }

      if (event.data.success) {
        finish(() => resolve(event.data));
        return;
      }

      finish(() => reject(new Error(event.data.error || "Google Drive authorization failed.")));
    };

    const closedInterval = window.setInterval(() => {
      if (popup.closed) {
        finish(() => reject(new Error("Google Drive authorization was canceled.")));
      }
    }, 500);

    const timeout = window.setTimeout(() => {
      try {
        popup.close();
      } catch (_) {
        // Ignore close failures while reporting the timeout.
      }
      finish(() => reject(new Error("Google Drive authorization timed out. Please try again.")));
    }, timeoutMs);

    window.addEventListener("message", handleMessage);
    popup.location.href = authorizationUrl;
    try {
      popup.focus();
    } catch (_) {
      // Ignore focus failures.
    }
  });
}

export function notifyGoogleDriveAuthOpener(result) {
  if (
    typeof window === "undefined"
    || !window.opener
    || window.opener.closed
  ) {
    return false;
  }

  window.opener.postMessage(
    {
      type: GOOGLE_DRIVE_AUTH_MESSAGE_TYPE,
      success: result?.success === true,
      error: result?.error || "",
    },
    window.location.origin
  );
  window.setTimeout(() => window.close(), 100);
  return true;
}

export async function disconnectGoogleDrive() {
  const disconnectFn = httpsCallable(functions, "googleDriveDisconnect");
  const result = await disconnectFn();
  return result.data;
}

export async function getGoogleDriveAccessToken() {
  const tokenFn = httpsCallable(functions, "googleDriveGetAccessToken");
  const result = await tokenFn();
  return result.data;
}

export async function prepareGoogleDriveFileForEmbed(options = {}) {
  const prepareFn = httpsCallable(functions, "googleDrivePrepareFileForEmbed");
  const result = await prepareFn(options);
  return result.data;
}

export async function createGoogleDriveFile(options = {}) {
  const createFn = httpsCallable(functions, "googleDriveCreateFile");
  const result = await createFn(options);
  return result.data;
}

export async function listGoogleDriveFolderImages(options = {}) {
  const listFn = httpsCallable(functions, "googleDriveListFolderImages");
  const result = await listFn(options);
  return result.data;
}

export async function createGoogleDriveImageProxyToken(options = {}) {
  const tokenFn = httpsCallable(functions, "googleDriveCreateImageProxyToken");
  const result = await tokenFn(options);
  return result.data;
}

export function buildGoogleDriveImageProxyUrl(token) {
  const trimmedToken = String(token || "").trim();
  if (!trimmedToken) return "";

  const encodedToken = encodeURIComponent(trimmedToken);
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "lesson-launchpad";
  if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true") {
    return `http://127.0.0.1:5001/${projectId}/us-central1/googleDriveImageProxy?token=${encodedToken}`;
  }

  return `https://us-central1-${projectId}.cloudfunctions.net/googleDriveImageProxy?token=${encodedToken}`;
}

function loadGooglePickerScript() {
  if (window.gapi?.load && window.google?.picker) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_PICKER_SCRIPT_ID);
    if (existingScript) {
      if (window.gapi?.load) {
        resolve();
        return;
      }
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Google Picker script failed to load")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_PICKER_SCRIPT_ID;
    script.src = "https://apis.google.com/js/api.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google Picker script failed to load"));
    document.body.appendChild(script);
  }).then(() => new Promise((resolve) => {
    window.gapi.load("picker", resolve);
  }));
}

function configureGoogleDriveDocsView(view, options = {}) {
  if (typeof view.setMimeTypes === "function" && options.mimeTypes) {
    view.setMimeTypes(options.mimeTypes);
  }
  if (typeof options.includeFolders === "boolean" && typeof view.setIncludeFolders === "function") {
    view.setIncludeFolders(options.includeFolders);
  }
  if (typeof options.ownedByMe === "boolean" && typeof view.setOwnedByMe === "function") {
    view.setOwnedByMe(options.ownedByMe);
  }
  if (options.selectFolderEnabled && typeof view.setSelectFolderEnabled === "function") {
    view.setSelectFolderEnabled(true);
  }
  if (options.label && typeof view.setLabel === "function") {
    view.setLabel(options.label);
  }
  return view;
}

function addGoogleDrivePickerViews(pickerBuilder, picker, { isFolderMode, mimeTypes }) {
  if (isFolderMode) {
    pickerBuilder.addView(configureGoogleDriveDocsView(
      new picker.DocsView(picker.ViewId.FOLDERS),
      {
        mimeTypes,
        includeFolders: true,
        selectFolderEnabled: true,
      }
    ));
    return;
  }

  pickerBuilder.addView(configureGoogleDriveDocsView(
    new picker.DocsView(picker.ViewId.DOCS),
    {
      mimeTypes,
      includeFolders: false,
      label: "Files",
    }
  ));
  pickerBuilder.addView(configureGoogleDriveDocsView(
    new picker.DocsView(picker.ViewId.DOCS),
    {
      mimeTypes,
      includeFolders: true,
      label: "My Drive",
    }
  ));
  pickerBuilder.addView(configureGoogleDriveDocsView(
    new picker.DocsView(picker.ViewId.DOCS),
    {
      mimeTypes,
      includeFolders: true,
      ownedByMe: false,
      label: "Shared with me",
    }
  ));

  const recentViewId = picker.ViewId?.RECENTLY_PICKED || picker.ViewId?.RECENT;
  if (recentViewId) {
    pickerBuilder.addView(configureGoogleDriveDocsView(
      new picker.DocsView(recentViewId),
      {
        mimeTypes,
        includeFolders: false,
        label: "Recent",
      }
    ));
  }
}

export async function openGoogleDrivePicker({ mode, title = "Choose Google file" } = {}) {
  await loadGooglePickerScript();

  const auth = await getGoogleDriveAccessToken();
  const allowedMimeTypes = getGoogleMimeTypesForPickerMode(mode);

  return new Promise((resolve, reject) => {
    if (!window.google?.picker) {
      reject(new Error("Google Picker is not available"));
      return;
    }

    const isFolderMode = mode === GOOGLE_PICKER_MODES.FOLDER;
    const pickerApi = window.google.picker;
    const pickerBuilder = new pickerApi.PickerBuilder();

    addGoogleDrivePickerViews(pickerBuilder, pickerApi, {
      isFolderMode,
      mimeTypes: allowedMimeTypes.join(","),
    });

    pickerBuilder
      .setOAuthToken(auth.accessToken)
      .setCallback((data) => {
        if (data.action === pickerApi.Action.PICKED) {
          const doc = data.docs?.[0];
          resolve({
            fileId: doc?.id || "",
            name: doc?.name || doc?.title || "",
            mimeType: doc?.mimeType || "",
          });
          return;
        }

        if (data.action === pickerApi.Action.CANCEL) {
          resolve(null);
        }
      })
      .setTitle(title);

    if (auth.developerKey) {
      pickerBuilder.setDeveloperKey(auth.developerKey);
    }

    if (auth.appId) {
      pickerBuilder.setAppId(auth.appId);
    }

    pickerBuilder.build().setVisible(true);
  });
}

/**
 * Open a placeholder tab during the user gesture so popup blockers allow Google later.
 * Do not pass noopener — that makes window.open return null and leaves a stuck blank tab.
 *
 * @param {string} loadingMessage
 * @param {{ imageUrl?: string }} [options]
 */
export function openGoogleEditorTab(loadingMessage = "Opening Google...", options = {}) {
  if (typeof window === "undefined") return null;
  const tab = window.open("", "_blank");
  if (!tab) return null;

  const message = String(loadingMessage || "Opening Google...");
  const imageUrl = String(options?.imageUrl || "").trim();
  const absoluteImageUrl = imageUrl
    ? (imageUrl.startsWith("http") || imageUrl.startsWith("data:")
      ? imageUrl
      : `${window.location.origin}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`)
    : "";

  try {
    tab.document.title = message;
    tab.document.body.style.margin = "0";
    tab.document.body.style.background = "#ffffff";
    tab.document.body.innerHTML = absoluteImageUrl
      ? `
      <div style="font-family: system-ui, -apple-system, sans-serif; display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:100vh; padding:24px; box-sizing:border-box; text-align:center; background:#ffffff;">
        <img
          src="${absoluteImageUrl.replace(/"/g, "&quot;")}"
          alt="${message.replace(/"/g, "&quot;")}"
          style="width:min(720px, 100%); height:auto; display:block;"
        />
      </div>
    `
      : `
      <div style="font-family: system-ui, sans-serif; display:flex; align-items:center; justify-content:center; min-height:100vh; color:#004aad; font-weight:600;">
        ${message.replace(/</g, "&lt;")}
      </div>
    `;
  } catch (_) {
    // Some browsers restrict writing to the placeholder tab; navigation still works.
  }

  return tab;
}

export function navigateGoogleEditorTab(tab, url) {
  const targetUrl = String(url || "").trim();
  if (!targetUrl) {
    throw new Error("Missing Google edit URL");
  }

  if (tab && !tab.closed) {
    tab.location.href = targetUrl;
    try {
      tab.focus();
    } catch (_) {
      // Ignore focus failures.
    }
    return tab;
  }

  const opened = typeof window !== "undefined" ? window.open(targetUrl, "_blank") : null;
  if (!opened) {
    throw new Error("Could not open Google in a new tab. Please allow popups for Lesson Launchpad.");
  }
  return opened;
}

export function closeGoogleEditorTab(tab) {
  if (tab && !tab.closed) {
    try {
      tab.close();
    } catch (_) {
      // Ignore close failures.
    }
  }
}
